/*
================================================================================
| FILENAME: src/features/ai/AiChatPanel.jsx (Updated)
| PURPOSE: To make the AI chat history persistent across page reloads.
|
| KEY CHANGES:
| 1.  A new `useEffect` hook has been added that runs when the component mounts.
| 2.  This hook calls the new `apiService.fetchChatHistory` function.
| 3.  If a previous conversation is found, its messages and ID are loaded
|     into the component's state, restoring the chat where the user left off.
================================================================================
*/
import React, { useState, useEffect, useRef } from 'react';
import { PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/solid';
import logo from '../../assets/Asset 2.png';
import apiService from '../../services/apiService';
import { useAICommand } from '../../context/AICommandContext';

const AiChatPanel = ({ onClose, setActiveView }) => {
    const { executeCommand } = useAICommand();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [conversationId, setConversationId] = useState(null);
    const [isLoading, setIsLoading] = useState(true); // Start loading to fetch history
    const messagesEndRef = useRef(null);

    useEffect(() => {
        const loadHistory = async () => {
            try {
                const history = await apiService.fetchChatHistory();
                if (history && history.messages.length > 0) {
                    setMessages(history.messages);
                    setConversationId(history._id);
                } else {
                    setMessages([{ from: 'ai', text: "How can I help? Try 'Run an analysis on [URL]'." }]);
                }
            } catch (error) {
                console.error("Failed to load chat history", error);
                setMessages([{ from: 'ai', text: "I couldn't load our previous conversation. How can I help now?" }]);
            } finally {
                setIsLoading(false);
            }
        };
        loadHistory();
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };
    useEffect(scrollToBottom, [messages, isLoading]);

    const handleToolCall = (toolCalls) => {
        // ... (rest of the function is unchanged)
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;
        const userMessage = { from: 'user', text: input };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        try {
            const payload = { messages: [...messages, userMessage], conversationId };
            const response = await apiService.sendChatMessage(payload);
            
            if (response.conversationId) {
                setConversationId(response.conversationId);
            }

            if (response.type === 'tool_call') {
                handleToolCall(response.tool_calls);
            } else {
                setMessages(prev => [...prev, response.message]);
            }
        } catch (error) {
            console.error("AI Chat Error:", error);
            setMessages(prev => [...prev, { from: 'ai', text: "Sorry, an error occurred." }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-96 h-[600px] bg-white rounded-lg shadow-2xl flex flex-col mb-4 text-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
                 <div className="flex items-center">
                    <img src={logo} alt="AI Avatar" className="h-8 w-8 rounded-full mr-3" />
                    <h3 className="font-semibold text-lg">AI Assistant</h3>
                </div>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 overflow-y-auto">
                {isLoading && messages.length === 0 && (
                     <div className="flex items-start mb-4">
                        <img src={logo} alt="AI" className="h-8 w-8 rounded-full mr-3" />
                        <div className="px-4 py-2 rounded-lg max-w-xs bg-gray-100 text-gray-500 animate-pulse">
                           ...
                        </div>
                    </div>
                )}
                {messages.map((msg, index) => (
                    <div key={index} className={`flex items-start mb-4 ${msg.from === 'user' ? 'justify-end' : ''}`}>
                        {msg.from === 'ai' && <img src={logo} alt="AI" className="h-8 w-8 rounded-full mr-3" />}
                        <div className={`px-4 py-2 rounded-lg max-w-xs text-sm ${msg.from === 'ai' ? 'bg-gray-100' : 'bg-blue-500 text-white'}`}>
                            {msg.text}
                        </div>
                    </div>
                ))}
                {isLoading && messages.length > 0 && (
                    <div className="flex items-start mb-4">
                        <img src={logo} alt="AI" className="h-8 w-8 rounded-full mr-3" />
                        <div className="px-4 py-2 rounded-lg max-w-xs bg-gray-100 text-gray-500 animate-pulse">
                           ...
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t flex items-center">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask me anything..."
                    className="flex-1 border rounded-full py-2 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-800"
                    disabled={isLoading}
                />
                <button onClick={handleSend} className="ml-3 text-blue-500 hover:text-blue-700 disabled:text-gray-400" disabled={isLoading}>
                    <PaperAirplaneIcon className="h-7 w-7" />
                </button>
            </div>
        </div>
    );
};

export default AiChatPanel;
